<template>
  <div class="p-2 sm:p-4">
    <PageHeader title="Gestion de stock" />

    <div class="bg-white rounded-lg p-6 shadow">
      <div class="flex gap-4 mb-6 flex-wrap">
        <div class="w-full sm:min-w-[200px] sm:max-w-[400px]">
          <label for="search" class="block mb-2 font-medium">Rechercher</label>
          <input
            id="search"
            v-model.trim="searchQuery"
            type="text"
            placeholder="Rechercher par référence"
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
          />
        </div>

        <div class="w-full sm:min-w-[200px] sm:max-w-[400px]">
          <label for="venduPar" class="block mb-2 font-medium"
            >Type de vente</label
          >
          <select
            id="venduPar"
            v-model="selectedVenduPar"
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
          >
            <option value="">Tous</option>
            <option value="couleurs">Vendu par couleur</option>
            <option value="tailles">Vendu par taille</option>
            <option value="melangees">Couleur mélangée</option>
          </select>
        </div>

        <div class="w-full sm:min-w-[200px] sm:max-w-[400px]">
          <label for="stockStatus" class="block mb-2 font-medium"
            >Statut du stock</label
          >
          <select
            id="stockStatus"
            v-model="selectedStockStatus"
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
          >
            <option value="">Tous</option>
            <option value="recorded">Stock enregistré</option>
            <option value="not_recorded">Stock non enregistré</option>
          </select>
        </div>

        <div class="flex items-end min-w-[120px]">
          <button
            @click="clearAllFilters"
            class="mt-6 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            <i class="fas fa-times mr-1"></i>
            Effacer les filtres
          </button>
        </div>
      </div>

      <div
        class="flex items-center gap-2 mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg"
      >
        <span class="text-sm font-medium text-amber-700">Stock</span>
        <span
          class="inline-block w-6 text-center font-bold text-amber-600 border border-amber-300 rounded bg-amber-100"
          >-</span
        >
        <span class="text-sm font-medium text-amber-700"
          >indique un produit dont le stock n'a pas encore été enregistré.</span
        >
      </div>

      <!-- Pagination (top) -->
      <UnifiedPagination
        :total-items="totalProducts"
        :current-page="currentPage"
        :items-per-page="itemsPerPage"
        variant="product"
        item-label="produits"
        :items-per-page-options="[10, 25, 50, 100]"
        :show-first-last-buttons="true"
        @update:currentPage="handlePageChange"
        @update:itemsPerPage="handleItemsPerPageChange"
        @page-change="handlePageChange"
        @items-per-page-change="handleItemsPerPageChange"
      />

      <!-- Product list -->
      <div class="space-y-2 mt-4">
        <!-- Loading state -->
        <div v-if="loading" class="flex items-center justify-center py-12">
          <span
            class="inline-block w-5 h-5 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin mr-3"
          ></span>
          <span class="text-gray-500">Chargement...</span>
        </div>

        <!-- Empty state -->
        <div
          v-else-if="filteredStocks.length === 0"
          class="text-center py-12 text-gray-400"
        >
          <i class="fas fa-box-open text-3xl mb-3"></i>
          <p>Aucun produit trouvé</p>
        </div>

        <!-- Product cards -->
        <template v-for="item in filteredStocks" :key="item.id">
          <template v-if="!item.isChild">
            <div
              :class="[
                'rounded-xl border transition-all duration-200 overflow-hidden',
                expandedReferenceId === item.id
                  ? item.venduPar === 'melangees'
                    ? 'border-purple-200 shadow-md ring-1 ring-purple-100'
                    : item.venduPar === 'tailles'
                    ? 'border-green-200 shadow-md ring-1 ring-green-100'
                    : 'border-blue-200 shadow-md ring-1 ring-blue-100'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm',
              ]"
            >
              <!-- Header row (always visible) -->
              <div
                class="flex items-center justify-between px-3 sm:px-5 py-1.5 sm:py-2 cursor-pointer select-none flex-wrap gap-2"
                :class="[
                  expandedReferenceId === item.id
                    ? item.venduPar === 'melangees'
                      ? 'bg-purple-50/60'
                      : item.venduPar === 'tailles'
                      ? 'bg-green-50/60'
                      : 'bg-blue-50/60'
                    : 'bg-white hover:bg-gray-50/80',
                ]"
                @click="toggleReferenceDropdown(item.id)"
              >
                <div class="flex items-center gap-3">
                  <button
                    @click.stop="toggleReferenceDropdown(item.id)"
                    :class="[
                      'w-7 h-7 flex items-center justify-center rounded-lg transition-all duration-200',
                      expandedReferenceId === item.id
                        ? item.venduPar === 'melangees'
                          ? 'bg-purple-500 text-white shadow-sm'
                          : item.venduPar === 'tailles'
                          ? 'bg-green-500 text-white shadow-sm'
                          : 'bg-blue-500 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600',
                    ]"
                  >
                    <i
                      :class="[
                        'fas text-[10px] transition-transform duration-200',
                        expandedReferenceId === item.id
                          ? 'fa-chevron-down'
                          : 'fa-chevron-right',
                      ]"
                    ></i>
                  </button>
                  <div
                    class="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gray-50 border border-gray-100"
                  >
                    <img
                      v-if="item.image"
                      :src="item.image"
                      :alt="item.referenceBase || item.reference"
                      loading="lazy"
                      class="w-full h-full object-cover"
                    />
                    <div
                      v-else
                      class="w-full h-full flex items-center justify-center"
                    >
                      <i class="fas fa-tshirt text-gray-300 text-xs"></i>
                    </div>
                  </div>
                  <span class="font-semibold text-gray-800">{{
                    item.referenceBase || item.reference
                  }}</span>
                  <span
                    v-if="item.packQuantites && item.packQuantites.length > 0"
                    :class="[
                      'inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border',
                      item.isMelangee
                        ? 'bg-purple-50 text-purple-600 border-purple-200'
                        : 'bg-blue-50 text-blue-600 border-blue-200',
                    ]"
                  >
                    <i
                      :class="[
                        'fas fa-box mr-1',
                        item.isMelangee ? 'text-purple-400' : 'text-blue-400',
                      ]"
                    ></i>
                    Qté paquet : {{ item.packQuantites.join("-") }}
                  </span>
                </div>
                <div class="flex items-center gap-4">
                  <span
                    class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
                    :class="getVenduParClass(item.venduPar)"
                  >
                    {{ getVenduParLabel(item.venduPar) }}
                  </span>
                  <span class="text-gray-400 text-sm">
                    {{ getColorCount(item) }} couleur(s)
                  </span>
                </div>
              </div>

              <!-- Expanded content -->
              <div v-if="expandedReferenceId === item.id" class="bg-white">
                <!-- Bundle count section for melangee products -->
                <div
                  v-if="item.isMelangee"
                  class="flex items-center justify-between px-3 sm:px-5 py-2 sm:py-3 bg-purple-50/40 border-t border-b border-purple-100/60 flex-wrap gap-2"
                >
                  <span class="text-sm font-semibold text-purple-700">
                    <i class="fas fa-cubes mr-1.5 text-purple-400"></i>Nombre de
                    paquets
                  </span>
                  <div class="flex items-center gap-3">
                    <label class="text-xs text-gray-500"
                      >Paquets en stock :</label
                    >
                    <input
                      v-model.number="item.bundleCount"
                      type="number"
                      min="0"
                      step="1"
                      class="w-20 px-2 py-1.5 text-sm border border-purple-200 rounded-lg text-center bg-white focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 transition-all"
                      @input="sanitizeNonNegativeInt(item, 'bundleCount')"
                      @keydown.enter.prevent
                    />
                    <button
                      @click="saveBundleCount(item)"
                      :disabled="savingBundle === item.id"
                      class="px-4 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 active:bg-purple-800 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                    >
                      <span
                        v-if="savingBundle === item.id"
                        class="inline-flex items-center"
                      >
                        <span
                          class="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5"
                        ></span>
                        Enregistrement...
                      </span>
                      <span v-else>Enregistrer paquets</span>
                    </button>
                  </div>
                </div>

                <!-- Color rows -->
                <div class="divide-y divide-gray-100 overflow-x-auto">
                  <div
                    v-for="(child, childIdx) in getChildItems(item)"
                    :key="childIdx"
                    :class="[
                      'flex items-center gap-3 sm:gap-4 px-3 sm:px-5 py-2 sm:py-3 transition-colors duration-150 min-w-0 flex-wrap sm:flex-nowrap',
                      item.venduPar === 'melangees'
                        ? 'hover:bg-purple-50/30'
                        : item.venduPar === 'tailles'
                        ? 'hover:bg-green-50/30'
                        : 'hover:bg-blue-50/30',
                    ]"
                  >
                    <!-- Photo -->
                    <div
                      class="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-50 border border-gray-100"
                    >
                      <img
                        v-if="child.image"
                        :src="child.image"
                        :alt="child.couleur || ''"
                        loading="lazy"
                        class="w-full h-full object-cover"
                      />
                      <div
                        v-else
                        class="w-full h-full flex items-center justify-center"
                      >
                        <i class="fas fa-tshirt text-gray-300 text-xs"></i>
                      </div>
                    </div>

                    <!-- Color name -->
                    <div class="min-w-[120px]">
                      <span
                        :class="[
                          'text-sm font-semibold',
                          item.venduPar === 'melangees'
                            ? 'text-purple-700'
                            : item.venduPar === 'couleurs'
                            ? 'text-blue-700'
                            : 'text-green-700',
                        ]"
                        >{{ child.couleur || "Sans couleur" }}</span
                      >
                      <span v-if="child.size" class="text-xs text-gray-400 ml-1"
                        >({{ child.size }})</span
                      >
                    </div>

                    <!-- Stock inputs (pushed to right) -->
                    <div class="flex-1 flex items-center justify-end gap-2">
                      <!-- Melangee type: taille stocks -->
                      <template
                        v-if="
                          item.isMelangee &&
                          child.tailleStocks &&
                          child.tailleStocks.length > 0
                        "
                      >
                        <span class="text-xs text-gray-400 mr-1"
                          >Quantités par taille :</span
                        >
                        <div class="flex items-center gap-1.5 flex-wrap">
                          <div
                            v-for="(ts, tidx) in child.tailleStocks"
                            :key="tidx"
                            class="flex items-center gap-1 bg-purple-50/60 border border-purple-100 rounded-lg px-2 py-1"
                          >
                            <span
                              class="text-[11px] text-purple-500 font-semibold"
                              >{{ ts.taille }}</span
                            >
                            <input
                              v-model.number="ts.quantity"
                              type="number"
                              min="0"
                              step="1"
                              class="w-11 px-1 py-0.5 text-xs border border-purple-200 rounded-md text-center bg-white focus:outline-none focus:ring-1 focus:ring-purple-300 transition-all"
                              @input="sanitizeNonNegativeInt(ts, 'quantity')"
                              @keydown.enter.prevent
                            />
                          </div>
                        </div>
                      </template>

                      <!-- Tailles type: taille stocks -->
                      <template
                        v-else-if="
                          item.venduPar === 'tailles' &&
                          child.tailleStocks &&
                          child.tailleStocks.length > 0
                        "
                      >
                        <span class="text-xs text-gray-400 mr-1"
                          >Stock par taille :</span
                        >
                        <div class="flex items-center gap-1.5 flex-wrap">
                          <div
                            v-for="(ts, tidx) in child.tailleStocks"
                            :key="tidx"
                            class="flex items-center gap-1 bg-green-50/60 border border-green-100 rounded-lg px-2 py-1"
                          >
                            <span
                              class="text-[11px] text-green-600 font-semibold"
                              >{{ ts.taille }}</span
                            >
                            <input
                              v-model.number="ts.quantity"
                              type="number"
                              min="0"
                              step="1"
                              :placeholder="ts.quantity === null ? '-' : ''"
                              class="w-11 px-1 py-0.5 text-xs border border-green-200 rounded-md text-center bg-white focus:outline-none focus:ring-1 focus:ring-green-300 transition-all"
                              @input="
                                sanitizeNonNegativeInt(ts, 'quantity');
                                debouncedAutoSaveTailleStock(child, ts);
                              "
                              @keydown.enter.prevent
                            />
                            <span
                              v-if="autoSaving[`${child.id}-${ts.taille}`]"
                              class="w-3 h-3 border-2 border-green-400 border-t-transparent rounded-full animate-spin inline-block"
                            ></span>
                          </div>
                        </div>
                      </template>

                      <!-- Couleurs type: taille stocks (si déclinaison existe) -->
                      <template
                        v-else-if="
                          item.venduPar === 'couleurs' &&
                          child.tailleStocks &&
                          child.tailleStocks.length > 0
                        "
                      >
                        <span class="text-xs text-gray-400 mr-1"
                          >Stock par taille :</span
                        >
                        <div class="flex items-center gap-1.5 flex-wrap">
                          <div
                            v-for="(ts, tidx) in child.tailleStocks"
                            :key="tidx"
                            class="flex items-center gap-1 bg-blue-50/60 border border-blue-100 rounded-lg px-2 py-1"
                          >
                            <span
                              class="text-[11px] text-blue-600 font-semibold"
                              >{{ ts.taille }}</span
                            >
                            <input
                              v-model.number="ts.quantity"
                              type="number"
                              min="0"
                              step="1"
                              :placeholder="ts.quantity === null ? '-' : ''"
                              class="w-11 px-1 py-0.5 text-xs border border-blue-200 rounded-md text-center bg-white focus:outline-none focus:ring-1 focus:ring-blue-300 transition-all"
                              @input="
                                sanitizeNonNegativeInt(ts, 'quantity');
                                debouncedAutoSaveTailleStock(child, ts);
                              "
                              @keydown.enter.prevent
                            />
                            <span
                              v-if="autoSaving[`${child.id}-${ts.taille}`]"
                              class="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin inline-block"
                            ></span>
                          </div>
                        </div>
                      </template>

                      <!-- Fallback: single stock input (couleurs sans déclinaison ou autre) -->
                      <template v-else>
                        <span class="text-xs text-gray-400 mr-1">Stock :</span>
                        <input
                          v-model.number="child.stock"
                          type="number"
                          min="0"
                          step="1"
                          :placeholder="child.stock === null ? '-' : ''"
                          :class="[
                            'w-20 px-2 py-1.5 text-sm border rounded-lg text-center bg-white focus:outline-none focus:ring-1 transition-all',
                            item.venduPar === 'melangees'
                              ? 'border-purple-200 focus:ring-purple-300'
                              : item.venduPar === 'couleurs'
                              ? 'border-blue-200 focus:ring-blue-300'
                              : 'border-green-200 focus:ring-green-300',
                          ]"
                          @change="updateStock(child)"
                          @keydown.enter.prevent
                        />
                      </template>
                    </div>
                  </div>
                </div>

                <!-- Save button -->
                <div
                  :class="[
                    'flex justify-end px-3 sm:px-5 py-2 sm:py-3 border-t',
                    item.venduPar === 'melangees'
                      ? 'bg-purple-50/30 border-purple-100/60'
                      : item.venduPar === 'tailles'
                      ? 'bg-green-50/30 border-green-100/60'
                      : 'bg-blue-50/30 border-blue-100/60',
                  ]"
                >
                  <button
                    @click="saveAllChildren(item)"
                    :disabled="saving === item.id"
                    :class="[
                      'px-6 py-2 text-sm font-semibold text-white rounded-lg shadow-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98]',
                      item.venduPar === 'melangees'
                        ? 'bg-purple-600 hover:bg-purple-700'
                        : item.venduPar === 'couleurs'
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-green-600 hover:bg-green-700',
                    ]"
                  >
                    <span
                      v-if="saving === item.id"
                      class="inline-flex items-center"
                    >
                      <span
                        class="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5"
                      ></span>
                      Enregistrement...
                    </span>
                    <span v-else
                      ><i class="fas fa-save mr-1.5"></i>Enregistrer</span
                    >
                  </button>
                </div>
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useNotification } from "@/composables/useNotification";
import { PageHeader, UnifiedPagination } from "@/components";
import {
  getProductsPage,
  getProduitCouleurs,
  saveProduitStocks,
  getProduitStocks,
  upsertProduitStock,
  updateCouleurDescriptionPaquet,
  type FilterProduitInput,
} from "@/api/produits";
import { getDeclinaisonById } from "@/api/gammesTailles";
import { getPackById } from "@/api/packs";
import { getProductPhotosBatch } from "@/api/shooting";
import { getProductImageUrl } from "@/config/api";

interface TailleStock {
  taille: string;
  quantity: number | null;
}

interface StockEntry {
  colorLabel: string;
  quantity: number;
  tailleStocks?: TailleStock[];
  idCouleur?: number;
}

interface ColorVariant {
  id: number;
  couleur: string;
}

interface StockItem {
  id: number;
  reference: string;
  referenceBase?: string;
  productName: string;
  venduPar: "couleurs" | "tailles" | "melangees";
  couleur?: string;
  size?: string;
  stock: number | null;
  stocks?: StockEntry[];
  colorVariants?: ColorVariant[];
  main?: boolean;
  liaison?: string;
  hasSiblings?: boolean;
  isChild?: boolean;
  isMelangee?: boolean;
  tailleStocks?: TailleStock[];
  idCouleur?: number;
  parentId?: number;
  bundleCount?: number;
  idPack?: number | null;
  packQuantites?: number[];
  image?: string;
  allPhotos?: string[];
}

const router = useRouter();
const authStore = useAuthStore();
const { success: notifySuccess, error: notifyError } = useNotification();

// Extraire les tailles non vides d'une déclinaison (d1_FR à d12_FR)
const extractTaillesFromDeclinaison = (declinaison: any): string[] => {
  if (!declinaison) return [];
  const tailles = [
    declinaison.d1_FR,
    declinaison.d2_FR,
    declinaison.d3_FR,
    declinaison.d4_FR,
    declinaison.d5_FR,
    declinaison.d6_FR,
    declinaison.d7_FR,
    declinaison.d8_FR,
    declinaison.d9_FR,
    declinaison.d10_FR,
    declinaison.d11_FR,
    declinaison.d12_FR,
  ].filter((t): t is string => !!t && t.trim() !== "");
  return [...new Set(tailles)];
};

// Extraire les quantités du pack (p1 à p12)
const extractPackQuantites = (pack: any): number[] => {
  if (!pack) return [];
  return [
    pack.p1,
    pack.p2,
    pack.p3,
    pack.p4,
    pack.p5,
    pack.p6,
    pack.p7,
    pack.p8,
    pack.p9,
    pack.p10,
    pack.p11,
    pack.p12,
  ].filter((v): v is number => v != null && v !== 0);
};

// Chercher un stock existant pour une taille donnée, sans fallback (exact match uniquement)
const findExactStock = (
  stocksData: any[],
  idCouleur: number,
  taille: string,
) => {
  if (idCouleur === 0) {
    return stocksData.find((s) => s.taille === taille);
  }
  return stocksData.find(
    (s) => s.id_couleur === idCouleur && s.taille === taille,
  );
};

// Chercher un stock existant pour une taille donnée, avec fallback si idCouleur === 0
const findExistingStock = (
  stocksData: any[],
  idCouleur: number,
  taille: string,
) => {
  if (idCouleur === 0) {
    // Si idCouleur est 0 (non défini), chercher uniquement par taille
    const byTaille = stocksData.find((s) => s.taille === taille);
    if (byTaille) return byTaille;
    // Fallback: chercher sans taille (stock enregistré avec taille: null)
    return stocksData.find((s) => !s.taille);
  }
  const exact = stocksData.find(
    (s) => s.id_couleur === idCouleur && s.taille === taille,
  );
  if (exact) return exact;
  // Fallback: chercher par id_couleur sans taille (stock enregistré avec taille: null)
  return stocksData.find((s) => s.id_couleur === idCouleur && !s.taille);
};

const loading = ref(false);
const saving = ref<number | null>(null);
const autoSaving = ref<Record<string, boolean>>({});
const savingBundle = ref<number | null>(null);
const searchQuery = ref("");
const selectedVenduPar = ref("");
const selectedStockStatus = ref("");
const selectedColor = ref("");
const stocks = ref<StockItem[]>([]);
const expandedReferenceId = ref<number | null>(null);

const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalProducts = ref(0);

const debounce = <T extends (...args: unknown[]) => void>(
  fn: T,
  delay: number,
) => {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

const debouncedLoadStocks = debounce(() => {
  currentPage.value = 1;
  expandedReferenceId.value = null;
  loadStocks();
}, 300);

const clearAllFilters = () => {
  searchQuery.value = "";
  selectedVenduPar.value = "";
  selectedStockStatus.value = "";
  selectedColor.value = "";
};

watch([searchQuery, selectedVenduPar, selectedStockStatus], () => {
  debouncedLoadStocks();
});

const toggleReferenceDropdown = (id: number) => {
  expandedReferenceId.value = expandedReferenceId.value === id ? null : id;
};

const getColorCount = (item: StockItem): number => {
  if (item.isMelangee && item.stocks) {
    return item.stocks.length;
  }
  const refBase =
    ((item.referenceBase || "").trim().toLowerCase() ||
      (item.reference || "").trim().toLowerCase()) +
    "||" +
    (item.venduPar || "couleurs");
  const group = stocks.value.filter((s) => {
    const sKey =
      ((s.referenceBase || "").trim().toLowerCase() ||
        (s.reference || "").trim().toLowerCase()) +
      "||" +
      (s.venduPar || "couleurs");
    return sKey === refBase && s.venduPar !== "melangees";
  });
  return group.length || 1;
};

const getChildItems = (item: StockItem): StockItem[] => {
  if (item.isMelangee && item.stocks) {
    return item.stocks.map((s, idx) => ({
      ...item,
      id: item.id * 1000 + idx,
      couleur: s.colorLabel,
      stock: s.quantity,
      stocks: undefined,
      tailleStocks: s.tailleStocks || [],
      isChild: true,
      isMelangee: true,
      idCouleur: s.idCouleur,
      parentId: item.id,
      image:
        item.allPhotos && item.allPhotos[idx] ? item.allPhotos[idx] : undefined,
    }));
  }

  const refBase =
    ((item.referenceBase || "").trim().toLowerCase() ||
      (item.reference || "").trim().toLowerCase()) +
    "||" +
    (item.venduPar || "couleurs");
  const group = stocks.value.filter((s) => {
    const sKey =
      ((s.referenceBase || "").trim().toLowerCase() ||
        (s.reference || "").trim().toLowerCase()) +
      "||" +
      (s.venduPar || "couleurs");
    return sKey === refBase && s.venduPar !== "melangees";
  });

  if (group.length > 0) {
    return group.map((child) => ({
      ...child,
      isChild: true,
      idCouleur: child.idCouleur,
    }));
  }

  return [{ ...item, isChild: true, idCouleur: item.idCouleur }];
};

// Computed: extract unique colors from loaded stocks
const availableColors = computed(() => {
  const uniqueColors = new Set<string>();
  stocks.value.forEach((item) => {
    if (item.couleur) {
      uniqueColors.add(item.couleur);
    }
  });
  return Array.from(uniqueColors).sort();
});

const filteredStocks = computed(() => {
  let result: StockItem[] = [];
  const processedRefBases = new Set<string>();

  stocks.value.forEach((item) => {
    const refBase =
      ((item.referenceBase || "").trim().toLowerCase() ||
        (item.reference || "").trim().toLowerCase()) +
      "||" +
      (item.venduPar || "couleurs");

    if (item.venduPar === "melangees") {
      if (processedRefBases.has(refBase)) return;

      if (item.stocks && item.stocks.length >= 1) {
        result.push({ ...item, hasSiblings: true, isMelangee: true });

        if (expandedReferenceId.value === item.id) {
          item.stocks.forEach((s, idx) => {
            result.push({
              ...item,
              id: item.id * 1000 + idx,
              couleur: s.colorLabel,
              stock: s.quantity,
              stocks: undefined,
              tailleStocks: s.tailleStocks || [],
              isChild: true,
              isMelangee: true,
            });
          });
        }
      } else {
        result.push({ ...item, hasSiblings: true, isMelangee: true });
      }

      processedRefBases.add(refBase);
      return;
    }

    if (processedRefBases.has(refBase)) return;

    const group = stocks.value.filter((s) => {
      const sKey =
        ((s.referenceBase || "").trim().toLowerCase() ||
          (s.reference || "").trim().toLowerCase()) +
        "||" +
        (s.venduPar || "couleurs");
      return sKey === refBase && s.venduPar !== "melangees";
    });

    if (group.length > 1) {
      let parentIndex = group.findIndex((g) => g.main);
      if (parentIndex === -1) parentIndex = 0;

      const parent = group[parentIndex];

      result.push({ ...parent, hasSiblings: true });
    } else {
      result.push({ ...item, hasSiblings: true });
    }

    processedRefBases.add(refBase);
  });

  if (selectedColor.value) {
    result = result.filter(
      (item) =>
        item.couleur?.toLowerCase() === selectedColor.value.toLowerCase(),
    );
  }

  return result;
});

const itemHasRecordedStock = (item: StockItem): boolean => {
  // For melangee: ALL stock entries must have ALL taille quantities non-null
  if (item.isMelangee && item.stocks) {
    return item.stocks.every(
      (s) =>
        s.tailleStocks &&
        s.tailleStocks.length > 0 &&
        s.tailleStocks.every((ts) => ts.quantity !== null),
    );
  }
  // For tailles: ALL children must have ALL tailleStocks non-null
  if (item.venduPar === "tailles") {
    const refBase = item.referenceBase || item.reference;
    const group = stocks.value.filter(
      (s) => (s.referenceBase || s.reference) === refBase,
    );
    return group.every((g) => {
      if (g.tailleStocks && g.tailleStocks.length > 0) {
        return g.tailleStocks.every((ts) => ts.quantity !== null);
      }
      return g.stock !== null;
    });
  }
  // For couleurs: ALL children must have stock non-null
  const refBase = item.referenceBase || item.reference;
  const group = stocks.value.filter(
    (s) => (s.referenceBase || s.reference) === refBase,
  );
  return group.every((g) => g.stock !== null);
};

const getVenduParLabel = (venduPar: string) => {
  const labels: Record<string, string> = {
    couleurs: "À la couleur",
    tailles: "À la taille",
    melangees: "Couleur mélangée",
  };
  return labels[venduPar] || venduPar;
};

const getVenduParClass = (venduPar: string) => {
  const classes: Record<string, string> = {
    couleurs: "bg-blue-100 text-blue-700",
    tailles: "bg-green-100 text-green-700",
    melangees: "bg-purple-100 text-purple-700",
  };
  return classes[venduPar] || "";
};

const handlePageChange = async (newPage: number) => {
  currentPage.value = newPage;
  expandedReferenceId.value = null;
  await loadStocks();
};

const handleItemsPerPageChange = async (newItemsPerPage: number) => {
  itemsPerPage.value = newItemsPerPage;
  currentPage.value = 1;
  expandedReferenceId.value = null;
  await loadStocks();
};

const loadStocks = async () => {
  loading.value = true;
  try {
    const id_vendeur = authStore.user?.id;
    const filter: FilterProduitInput = {
      id_vendeur,
      take: itemsPerPage.value,
      skip: (currentPage.value - 1) * itemsPerPage.value,
      orderBy: "dateCreation",
      orderDir: "DESC",
    };

    if (searchQuery.value && searchQuery.value.trim()) {
      (filter as any).reference = searchQuery.value.trim();
    }

    if (selectedVenduPar.value) {
      (filter as any).vendu_par = selectedVenduPar.value;
    }

    if (selectedStockStatus.value) {
      (filter as any).stockStatus =
        selectedStockStatus.value === "recorded" ? "recorded" : "not_recorded";
    }

    const page = await getProductsPage(filter);

    totalProducts.value = page.total;

    // First pass: create basic items
    const items: StockItem[] = page.items.map((p: any) => {
      const venduPar = p.vendu_par || "couleurs";
      const productId = p.id_produit || p.id;
      const item: StockItem = {
        id: productId,
        reference: p.reference || "",
        referenceBase: p.reference_base || "",
        productName: p.nom || "",
        venduPar,
        couleur: p.couleur || "",
        size: p.taille || "",
        stock: p.stock_value ?? p.stock ?? null,
        liaison: p.liaison || "",
        main: p.liaison && String(productId) === String(p.liaison),
        idCouleur: p.id_couleur || 0,
      };

      // Stocker id_pack pour les produits melangees et couleurs
      if ((venduPar === "melangees" || venduPar === "couleurs") && p.id_pack) {
        item.idPack = p.id_pack;
      }

      return item;
    });

    // Pour les produits melangees, charger les couleurs avec description_paquet
    const melangeeItems = items.filter((item) => item.venduPar === "melangees");
    await Promise.all(
      melangeeItems.map(async (item) => {
        try {
          // Récupérer les couleurs du produit avec description_paquet
          const couleurs = await getProduitCouleurs(item.id);

          if (!couleurs || couleurs.length === 0) {
            return;
          }

          // Récupérer la déclinaison (tailles) du produit
          const produit = page.items.find(
            (p: any) => (p.id_produit || p.id) === item.id,
          );
          const idDeclinaison = produit?.id_declinaison;

          let taillesList: string[] = [];
          if (idDeclinaison) {
            const declinaison = await getDeclinaisonById(idDeclinaison);
            taillesList = extractTaillesFromDeclinaison(declinaison);
          }

          // Construire le tableau stocks avec tailleStocks
          item.stocks = couleurs.map((c) => {
            const colorLabel = c.couleur?.couleur_FR || "";
            const idCouleur = c.id_couleur || 0;
            let tailleStocks: TailleStock[] = [];

            // Parser description_paquet pour obtenir les quantités par taille
            if (c.description_paquet && taillesList.length > 0) {
              try {
                let quantites: number[] = [];
                if (typeof c.description_paquet === "string") {
                  if (c.description_paquet.startsWith("[")) {
                    quantites = JSON.parse(c.description_paquet);
                  } else {
                    // Format "1,2,3" ou "1-2-3"
                    quantites = c.description_paquet
                      .split(/[-,]/)
                      .map((q: string) => parseInt(q.trim(), 10) || 0);
                  }
                }

                // Mapper les quantités aux tailles
                tailleStocks = taillesList.map((taille, idx) => ({
                  taille,
                  quantity: quantites[idx] || 0,
                }));
              } catch (e) {
                console.error(
                  "Erreur lors du parsing de description_paquet:",
                  e,
                );
              }
            } else if (taillesList.length > 0) {
              // Initialiser avec null (pas de données en base)
              tailleStocks = taillesList.map((taille) => ({
                taille,
                quantity: null,
              }));
            }

            return {
              colorLabel,
              idCouleur,
              quantity: tailleStocks.reduce((sum, ts) => sum + ts.quantity, 0),
              tailleStocks,
            };
          });

          // Charger les quantités du pack pour la validation
          if (item.idPack) {
            try {
              const pack = await getPackById(item.idPack);
              item.packQuantites = extractPackQuantites(pack);
            } catch (packErr) {
              console.error(
                `Erreur lors du chargement du pack pour le produit ${item.id}:`,
                packErr,
              );
            }
          }

          // Charger le nombre de paquets (bundleCount) depuis produits_stock avec id_couleur = 43
          try {
            const stocksData = await getProduitStocks(item.id);
            // id_couleur 43 est l'ID spécial pour les couleurs mélangées
            const melangeeStock = stocksData.find((s) => s.id_couleur === 43);
            item.bundleCount = melangeeStock?.value ?? 0;
          } catch (stockErr) {
            console.error(
              `Erreur lors du chargement du stock pour le produit ${item.id}:`,
              stockErr,
            );
            item.bundleCount = 0;
          }
        } catch (err) {
          console.error(
            `Erreur lors du chargement des couleurs pour le produit ${item.id}:`,
            err,
          );
        }
      }),
    );

    // Pour les produits tailles, charger les stocks par taille pour chaque couleur
    const taillesItems = items.filter((item) => item.venduPar === "tailles");
    await Promise.all(
      taillesItems.map(async (item) => {
        try {
          const produit = page.items.find(
            (p: any) => (p.id_produit || p.id) === item.id,
          );
          const idDeclinaison = produit?.id_declinaison;

          let taillesList: string[] = [];
          if (idDeclinaison) {
            const declinaison = await getDeclinaisonById(idDeclinaison);
            taillesList = extractTaillesFromDeclinaison(declinaison);
          }

          if (taillesList.length === 0) return;

          const stocksData = await getProduitStocks(item.id);

          const hasMultipleTailles = taillesList.length > 1;
          item.tailleStocks = taillesList.map((taille) => {
            const existingStock = hasMultipleTailles
              ? findExactStock(stocksData, item.idCouleur ?? 0, taille)
              : findExistingStock(stocksData, item.idCouleur ?? 0, taille);
            return {
              taille,
              quantity:
                existingStock !== undefined ? existingStock.value : null,
            };
          });

          item.stock = item.tailleStocks.reduce(
            (sum, ts) => sum + (ts.quantity ?? 0),
            0,
          );
        } catch (err) {
          console.error(
            `Erreur lors du chargement des tailles pour le produit ${item.id}:`,
            err,
          );
        }
      }),
    );

    // Pour les produits couleurs, charger aussi les tailles et packs si id_declinaison existe
    const couleursItems = items.filter((item) => item.venduPar === "couleurs");
    await Promise.all(
      couleursItems.map(async (item) => {
        try {
          const produit = page.items.find(
            (p: any) => (p.id_produit || p.id) === item.id,
          );
          const idDeclinaison = produit?.id_declinaison;

          if (!idDeclinaison) {
            // Pas de déclinaison : charger le stock simple
            // Compter les couleurs du produit pour déterminer la logique
            try {
              const produitCouleurs = await getProduitCouleurs(item.id);
              const nbCouleurs =
                produitCouleurs?.filter((pc: any) => pc.id_couleur != null)
                  .length ?? 0;

              if (nbCouleurs <= 1) {
                // Une seule couleur (ou aucune) : utiliser stock_value (SUM) comme ProductListView
                const produitData = page.items.find(
                  (p: any) => (p.id_produit || p.id) === item.id,
                );
                const stockRenseigne = (produitData as any)?.stock_renseigne;
                const stockValue = produitData?.stock_value;
                item.stock =
                  stockRenseigne && stockValue != null
                    ? Number(stockValue)
                    : null;
              } else {
                // Plusieurs couleurs : charger depuis produits_stock par id_couleur
                const stocksData = await getProduitStocks(item.id);
                if (stocksData.length > 0) {
                  const match =
                    item.idCouleur && item.idCouleur !== 0
                      ? stocksData.find((s) => s.id_couleur === item.idCouleur)
                      : stocksData[0];
                  if (match) {
                    item.stock = match.value ?? null;
                  }
                }
              }
            } catch (stockErr) {
              console.error(
                `Erreur lors du chargement du stock simple pour le produit couleur ${item.id}:`,
                stockErr,
              );
            }
            return;
          }

          let taillesList: string[] = [];
          const declinaison = await getDeclinaisonById(idDeclinaison);
          taillesList = extractTaillesFromDeclinaison(declinaison);

          if (taillesList.length === 0) return;

          const stocksData = await getProduitStocks(item.id);

          const hasMultipleTailles = taillesList.length > 1;
          item.tailleStocks = taillesList.map((taille) => {
            const existingStock = hasMultipleTailles
              ? findExactStock(stocksData, item.idCouleur ?? 0, taille)
              : findExistingStock(stocksData, item.idCouleur ?? 0, taille);
            return {
              taille,
              quantity:
                existingStock !== undefined ? existingStock.value : null,
            };
          });

          // Pour les produits avec une seule taille : si aucun stock par taille n'a été trouvé,
          // utiliser stock_value du produit (synchronisé depuis ProductListView)
          if (
            taillesList.length === 1 &&
            item.tailleStocks.length === 1 &&
            item.tailleStocks[0].quantity === null
          ) {
            const produitData = page.items.find(
              (p: any) => (p.id_produit || p.id) === item.id,
            );
            const stockRenseigne = (produitData as any)?.stock_renseigne;
            const stockValue = produitData?.stock_value;
            if (stockRenseigne && stockValue != null) {
              item.tailleStocks[0].quantity = Number(stockValue);
            }
          }

          item.stock = item.tailleStocks.reduce(
            (sum, ts) => sum + (ts.quantity ?? 0),
            0,
          );

          if (item.idPack) {
            try {
              const pack = await getPackById(item.idPack);
              item.packQuantites = extractPackQuantites(pack);
            } catch (packErr) {
              console.error(
                `Erreur lors du chargement du pack pour le produit couleur ${item.id}:`,
                packErr,
              );
            }
          }
        } catch (err) {
          console.error(
            `Erreur lors du chargement des tailles pour le produit couleur ${item.id}:`,
            err,
          );
        }
      }),
    );

    // Second pass: group by reference_base to build colorVariants
    const refBaseGroups = new Map<string, StockItem[]>();
    items.forEach((item) => {
      const key = item.referenceBase || item.reference;
      if (!refBaseGroups.has(key)) {
        refBaseGroups.set(key, []);
      }
      refBaseGroups.get(key)!.push(item);
    });

    // Assign colorVariants to each item
    items.forEach((item) => {
      const key = item.referenceBase || item.reference;
      const group = refBaseGroups.get(key) || [];
      item.colorVariants = group.map((g) => ({
        id: g.id,
        couleur: g.couleur || "",
      }));
    });

    // Charger les photos en batch pour tous les produits
    try {
      const allIds = items.map((item) => item.id);
      const { success, photosByProduct } = await getProductPhotosBatch(allIds);

      // Pour les produits mélangées, charger toutes les photos (full=true)
      const melangeeIds = items
        .filter((item) => item.venduPar === "melangees")
        .map((item) => item.id);
      let fullPhotosByProduct: Record<number, string[]> = {};
      if (melangeeIds.length > 0) {
        const fullResult = await getProductPhotosBatch(melangeeIds, true);
        if (fullResult.success) {
          fullPhotosByProduct = fullResult.photosByProduct;
        }
      }

      if (success && photosByProduct) {
        for (const item of items) {
          if (item.venduPar === "melangees" && fullPhotosByProduct[item.id]) {
            item.allPhotos = fullPhotosByProduct[item.id].map((p) =>
              getProductImageUrl(String(p)),
            );
            item.image = item.allPhotos[0] || undefined;
          } else {
            const photos = photosByProduct[item.id];
            const first =
              Array.isArray(photos) && photos.length ? photos[0] : undefined;
            if (first) {
              item.image = getProductImageUrl(String(first));
            }
          }
        }
      }
    } catch {
      // Ignorer les erreurs de chargement d'images
    }

    stocks.value = items;
  } catch (error) {
    console.error("Erreur lors du chargement des stocks:", error);
  } finally {
    loading.value = false;
  }
};

const autoSaveTailleStock = async (child: StockItem, ts: TailleStock) => {
  const key = `${child.id}-${ts.taille}`;
  autoSaving.value[key] = true;
  try {
    const idCouleur = child.idCouleur || child.colorVariants?.[0]?.id || 0;
    if (!idCouleur) {
      console.error("No color ID found for auto-save tailles item:", child);
      return;
    }
    const quantity = Math.max(0, Math.floor(Number(ts.quantity) || 0));
    await upsertProduitStock(child.id, idCouleur, quantity, ts.taille);

    notifySuccess("Stock enregistré avec succès!");

    // Update local state
    const stockItem = stocks.value.find((s) => s.id === child.id);
    if (stockItem && stockItem.tailleStocks) {
      const localTs = stockItem.tailleStocks.find(
        (t) => t.taille === ts.taille,
      );
      if (localTs) localTs.quantity = quantity;
      stockItem.stock = stockItem.tailleStocks.reduce(
        (sum, t) => sum + (t.quantity ?? 0),
        0,
      );
    }
  } catch (error) {
    console.error("Error auto-saving taille stock:", error);
    notifyError("Erreur lors de l'enregistrement automatique du stock");
  } finally {
    autoSaving.value[key] = false;
  }
};

const tailleDebounceTimers = new Map<string, ReturnType<typeof setTimeout>>();
const debouncedAutoSaveTailleStock = (child: StockItem, ts: TailleStock) => {
  const key = `${child.id}-${ts.taille}`;
  const existing = tailleDebounceTimers.get(key);
  if (existing) clearTimeout(existing);
  tailleDebounceTimers.set(
    key,
    setTimeout(() => {
      tailleDebounceTimers.delete(key);
      autoSaveTailleStock(child, ts);
    }, 800),
  );
};

const sanitizeNonNegativeInt = (obj: any, key: string) => {
  const val = obj[key];
  if (val === null || val === undefined) return;
  if (val === "" || isNaN(val) || val < 0) {
    obj[key] = 0;
  } else {
    obj[key] = Math.floor(val);
  }
};

const updateStock = (item: StockItem) => {
  if (item.stock === null || item.stock === undefined) {
    return;
  }
  if (item.stock < 0 || isNaN(item.stock)) {
    item.stock = 0;
  } else {
    item.stock = Math.floor(item.stock);
  }
  // Sync edited value back to stocks.value (template uses spread copies from getChildItems)
  const stockItem = stocks.value.find((s) => s.id === item.id);
  if (stockItem) {
    stockItem.stock = item.stock;
  }
};

const saveAllChildren = async (parentItem: StockItem) => {
  saving.value = parentItem.id;
  try {
    if (parentItem.isMelangee) {
      // Validation: vérifier que la somme des quantités des couleurs = quantités du pack
      if (
        parentItem.packQuantites &&
        parentItem.packQuantites.length > 0 &&
        parentItem.stocks &&
        parentItem.stocks.length > 0
      ) {
        const packQtes = parentItem.packQuantites;
        const sommeQuantites: number[] = new Array(packQtes.length).fill(0);

        for (const s of parentItem.stocks) {
          if (s.tailleStocks && s.tailleStocks.length > 0) {
            s.tailleStocks.forEach((ts, idx) => {
              if (idx < sommeQuantites.length) {
                sommeQuantites[idx] += ts.quantity ?? 0;
              }
            });
          }
        }

        let mismatch = false;
        for (let i = 0; i < packQtes.length; i++) {
          if (packQtes[i] !== sommeQuantites[i]) {
            mismatch = true;
            break;
          }
        }

        if (mismatch) {
          notifyError(
            `La somme des quantités des couleurs (${sommeQuantites.join(
              "-",
            )}) ne correspond pas aux quantités du paquet (${packQtes.join(
              "-",
            )})`,
          );
          saving.value = null;
          return;
        }
      }

      // For melangee, use getChildItems (stocks are in parentItem.stocks)
      const children = getChildItems(parentItem);
      for (const child of children) {
        await saveStock(child);
      }
    } else {
      // For couleurs/tailles, read directly from stocks.value to get edited values
      const refBase = parentItem.referenceBase || parentItem.reference;
      const group = stocks.value.filter(
        (s) =>
          (s.referenceBase || s.reference) === refBase &&
          s.venduPar !== "melangees",
      );
      const children = group.length > 0 ? group : [parentItem];
      for (const child of children) {
        await saveStock({
          ...child,
          isChild: true,
          idCouleur: child.idCouleur,
        });
      }
    }
    notifySuccess("Stock enregistré avec succès!");
  } catch (error) {
    console.error("Error saving all children:", error);
    notifyError("Erreur lors de l'enregistrement du stock");
  } finally {
    saving.value = null;
  }
};

const saveStock = async (item: StockItem) => {
  try {
    if (item.isMelangee && item.tailleStocks && item.tailleStocks.length > 0) {
      // Melangees type: update description_paquet in couleurs_produits
      const productId = item.parentId || item.id;
      const idCouleur = item.idCouleur;

      if (!idCouleur) {
        console.error("No color ID found for item:", item);
        notifyError("Erreur: ID couleur non trouvé");
        return;
      }

      // Build new description_paquet from tailleStocks
      const newDescriptionPaquet = item.tailleStocks
        .map((ts) => ts.quantity ?? 0)
        .join("-");

      // Use the new API to update only this color's description_paquet
      await updateCouleurDescriptionPaquet(
        productId,
        idCouleur,
        newDescriptionPaquet,
      );

      // Update local state
      const parentItem = stocks.value.find((s) => s.id === productId);
      if (parentItem && parentItem.stocks) {
        const stockEntry = parentItem.stocks.find(
          (s) => s.idCouleur === item.idCouleur,
        );
        if (stockEntry) {
          stockEntry.tailleStocks = [...item.tailleStocks];
          stockEntry.quantity = item.tailleStocks.reduce(
            (sum, ts) => sum + (ts.quantity ?? 0),
            0,
          );
        }
      }
    } else if (item.tailleStocks && item.tailleStocks.length > 0) {
      // Tailles type: update produits_stock for each taille
      const idCouleur = item.idCouleur || item.colorVariants?.[0]?.id || 0;

      if (!idCouleur) {
        console.error("No color ID found for tailles item:", item);
        notifyError("Erreur: ID couleur non trouvé");
        return;
      }

      // Save each taille stock using upsertProduitStock (skip null quantities)
      const taillesToSave = item.tailleStocks.filter(
        (ts) => ts.quantity !== null && ts.quantity !== undefined,
      );
      if (taillesToSave.length === 0) return;
      await Promise.all(
        taillesToSave.map(async (ts) => {
          await upsertProduitStock(
            item.id,
            idCouleur,
            ts.quantity ?? 0,
            ts.taille,
          );
        }),
      );

      // Update local state
      const stockItem = stocks.value.find((s) => s.id === item.id);
      if (stockItem) {
        stockItem.tailleStocks = [...item.tailleStocks];
        stockItem.stock = item.tailleStocks.reduce(
          (sum, ts) => sum + (ts.quantity ?? 0),
          0,
        );
      }
    } else {
      // Couleurs type: update produits_stock with single value (upsert to avoid deleting other colors)
      const idCouleur = item.idCouleur || item.colorVariants?.[0]?.id || 0;

      if (!idCouleur) {
        console.error("No color ID found for couleurs item:", item);
        return;
      }

      if (item.stock === null || item.stock === undefined) {
        return; // Ne pas enregistrer si aucune valeur n'a été saisie
      }
      const stockValue = item.stock < 0 ? 0 : item.stock || 0;
      await upsertProduitStock(
        item.id,
        idCouleur,
        stockValue,
        item.size || null,
      );

      // Update local state
      const stockItem = stocks.value.find((s) => s.id === item.id);
      if (stockItem) {
        stockItem.stock = item.stock;
      }
    }
  } catch (error) {
    console.error("Error saving stock:", error);
    throw error;
  }
};

// Sauvegarder le nombre de paquets (捆数) pour les produits couleur mélangée
const saveBundleCount = async (item: StockItem) => {
  savingBundle.value = item.id;
  try {
    const bundleCount = item.bundleCount ?? 0;

    // id_couleur 43 est l'ID spécial pour les couleurs mélangées
    const ID_COULEUR_MELANGEES = 43;

    // Utiliser upsertProduitStock pour ne pas supprimer les autres enregistrements de stock
    await upsertProduitStock(item.id, ID_COULEUR_MELANGEES, bundleCount, null);

    // Mettre à jour l'état local
    const stockItem = stocks.value.find((s) => s.id === item.id);
    if (stockItem) {
      stockItem.bundleCount = bundleCount;
    }

    notifySuccess("Nombre de paquets enregistré avec succès!");
  } catch (error) {
    console.error("Error saving bundle count:", error);
    notifyError("Erreur lors de l'enregistrement du nombre de paquets");
  } finally {
    savingBundle.value = null;
  }
};

onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth();
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: "auth" });
    return;
  }

  await loadStocks();
});
</script>
